import { computed, effect, inject, untracked } from '@angular/core';

import {
  signalStore,
  withState,
  withMethods,
  patchState,
  withComputed,
  withHooks,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';
import { forkJoin, pipe } from 'rxjs';
import {
  AppError,
  SpinnerService,
  setPending,
  setSuccess,
  setError,
  withRequestHandler,
  withRequestStatus,
} from '@hglobal/common';

import { PreferencesState, UpdatePreferencesReq } from './preferences.abstraction';
import { PreferencesService } from './preferences.service';

const initialState: PreferencesState = {
  preferences: null,
  languages: [],
};

export const PreferencesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withRequestHandler(),
  withComputed(({ preferences }) => ({
    currentLanguage: computed(() => preferences()?.language),
  })),
  withMethods(store => {
    const preferencesService = inject(PreferencesService);
    const spinnerService = inject(SpinnerService);

    return {
      loadInitialData(): void {
        patchState(store, setPending());

        forkJoin([
          preferencesService.getPreferences(),
          preferencesService.getLanguages(),
        ]).subscribe(([preferences, languages]) => {
          patchState(store, { preferences, languages }, setSuccess());
        });
      },
      updatePreferences: rxMethod<UpdatePreferencesReq>(
        pipe(
          switchMap(preferences => {
            patchState(store, setPending());

            return spinnerService.observe(
              preferencesService.updatePreferences(preferences),
              true,
            ).pipe(
              tapResponse({
                next: updatedPreferences => patchState(store, {
                  preferences: updatedPreferences,
                }),
                error: (error: AppError) => patchState(store, setError(error)),
              }),
            );
          }),
        ),
      ),
      clear: () => {
        patchState(store, initialState);
      },
    };
  }),
  withHooks({
    onInit(store) {
      effect(() => {
        if (store.error()) {
          untracked(() => store.handleError(store.error()));
        }
      });
    },
  }),
);
